import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, LogIn, Lock, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { setSession, isAuthenticated, formatApiErrorDetail } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated()) navigate('/admin', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      setSession(data.access_token, data.email);
      toast.success('Accesso effettuato');
      navigate('/admin', { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#1A1A2E] flex items-center justify-center px-4 noise-bg"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={18} /> Torna al sito
        </Link>

        <div className="bg-white border-2 border-gray-200 p-8 md:p-10 shadow-2xl">
          <div className="accent-line mb-6"></div>
          <h1 className="text-3xl font-extrabold text-[#1A1A2E] mb-2">Admin Dashboard</h1>
          <p className="text-sm text-[#555555] mb-8">
            Area riservata. Inserisci le credenziali per continuare.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-bold text-sm text-[#1A1A2E] mb-2 block">Email</label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="input-industrial w-full pl-10"
                  placeholder="tu@azienda.it"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="font-bold text-sm text-[#1A1A2E] mb-2 block">Password</label>
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]"
                />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="input-industrial w-full pl-10"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="bg-red-50 border-l-4 border-[#E94560] px-4 py-3 text-sm text-[#E94560]"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} /> Accesso in corso...
                </>
              ) : (
                <>
                  <LogIn size={18} /> Accedi
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          SELL&REC — Dashboard protetta. Accesso autorizzato solo al personale.
        </p>
      </div>
    </div>
  );
}
